import {
  type SubmissionResult,
  getFormProps,
  getInputProps,
  useForm,
} from "@conform-to/react";
import { CloseButton } from "@headlessui/react";
import { Trans, t } from "@lingui/macro";
import { Button } from "@winamp/ui/components/Button/Button.js";
import { Dialog } from "@winamp/ui/components/Dialog/Dialog.js";
import { DialogActions } from "@winamp/ui/components/Dialog/DialogActions.js";
import { DialogBody } from "@winamp/ui/components/Dialog/DialogBody.js";
import { DialogDescription } from "@winamp/ui/components/Dialog/DialogDescription.js";
import { DialogTitle } from "@winamp/ui/components/Dialog/DialogTitle.js";
import { ErrorMessages } from "@winamp/ui/components/Fieldset/ErrorMessages.js";
import { Field } from "@winamp/ui/components/Fieldset/Field.js";
import { Fieldset } from "@winamp/ui/components/Fieldset/Fieldset.js";
import { FormErrorsMessage } from "@winamp/ui/components/Fieldset/FormErrorsMessage.js";
import { Label } from "@winamp/ui/components/Fieldset/Label.js";
import { Input } from "@winamp/ui/components/Input/Input.js";
import { getValibotConstraint, parseWithValibot } from "conform-to-valibot";
import {
  type ComponentProps,
  type ReactNode,
  useEffect,
  useState,
} from "react";
import { useFetcher } from "react-router";
import { $path } from "safe-routes";
import type { ILink } from "~/models/link";
import {
  type action as EditArtistLinkAction,
  getArtistLinkSchema,
} from "~/routes/resources+/artists.$artistId.links.$linkId";

type LinksLibraryDeleteDialogProps = {
  artistUid: string;
  link: ILink;
  children: ReactNode | (({ open }: { open: () => void }) => ReactNode);
  onComplete?: () => void;
} & Omit<
  ComponentProps<typeof Dialog>,
  "children" | "open" | "onClose" | "unmount"
>;

export function LinksLibraryEditDialog({
  artistUid,
  link,
  children,
  onComplete,
  ...props
}: LinksLibraryDeleteDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [lastResult, setLastResult] = useState<
    SubmissionResult<Array<string>> | null | undefined
  >(null);

  const formAction = $path("/resources/artists/:artistId/links/:linkId", {
    artistId: artistUid,
    linkId: link.uid,
  });
  const fetcher = useFetcher<typeof EditArtistLinkAction>({
    key: `${artistUid}-${link.uid}-editLink-action`,
  });
  const isPending = fetcher.state !== "idle";

  const handleClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    setLastResult(fetcher.data);

    // Close after successful actions
    if (fetcher.data?.status === "success") {
      onComplete?.();
      handleClose();
    }
  }, [fetcher.data]);

  const ArtistLinkSchema = getArtistLinkSchema();

  const [form, fields] = useForm({
    defaultValue: {
      url: link.url,
      label: link.label,
    },
    id: [artistUid, link.uid, "editLink", isOpen].join("-"),
    lastResult,
    constraint: getValibotConstraint(ArtistLinkSchema),
    shouldValidate: "onBlur",
    onValidate: ({ formData }) => {
      const validation = parseWithValibot(formData, {
        schema: ArtistLinkSchema,
      });

      return validation;
    },
  });

  return (
    <>
      {typeof children === "function"
        ? children({ open: () => setIsOpen(!isOpen) })
        : children}
      <Dialog {...props} open={isOpen} onClose={() => handleClose()}>
        <fetcher.Form
          method="post"
          action={formAction}
          {...getFormProps(form)}
        />
        <DialogTitle>
          <Trans>Edit a Link</Trans>
        </DialogTitle>
        <DialogDescription>
          <Trans>
            Once a Link is edited, it's also changed where it is used like the
            Fanzone or the Winamp.link page.
          </Trans>
        </DialogDescription>
        <DialogBody>
          <Fieldset disabled={isPending} className="flex flex-col gap-4">
            <Field>
              <Label>
                <Trans>Link title</Trans>
              </Label>
              <Input
                {...getInputProps(fields.label, { type: "text" })}
                key={fields.label.key}
                placeholder={t`Link title`}
              />
              <ErrorMessages errors={fields.label.errors} />
            </Field>
            <Field>
              <Label>
                <Trans>Link URL</Trans>
              </Label>
              <Input
                {...getInputProps(fields.url, { type: "text" })}
                key={fields.url.key}
                placeholder={t`URL`}
              />
              <ErrorMessages errors={fields.url.errors} />
            </Field>

            <FormErrorsMessage errors={form.errors} />
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <CloseButton as={Button} variant="secondary">
            <Trans>Cancel</Trans>
          </CloseButton>
          <Button
            type="submit"
            form={form.id}
            name="action"
            value="edit"
            loading={isPending}
          >
            <Trans>Save</Trans>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
